import { Injectable } from '@angular/core';
import posthog from 'posthog-js';
import { BehaviorSubject, map, mergeMap, tap } from 'rxjs';
import { CoreItemType } from '../core-item/core-item';
import { CoreItemService } from '../core-item/core-item.service';
import { PostHogEvent } from '../posthog/posthog.event';
import { Goal, GoalSubData } from './goal';

@Injectable({
  providedIn: 'root',
})
export class GoalService {
  private _changeSubject = new BehaviorSubject<Goal | undefined>(undefined);

  constructor(private coreItemService: CoreItemService) {}

  getGoalById$(id: string) {
    return this.coreItemService.getItemByAction$({ id }).pipe(map((c) => c as Goal));
  }

  goalList$() {
    return this._changeSubject.asObservable().pipe(
      mergeMap((_) => {
        return this.coreItemService.getCoreItemList$([CoreItemType.goal]).pipe(
          map((list) => (list ?? []).filter((item) => item.coreType === CoreItemType.goal) as Goal[]),
          map((list) => list.sort((a, b) => a.subData.position.localeCompare(b.subData.position)))
        );
      })
    );
  }

  updateGoal$(goal: Goal) {
    posthog.capture(PostHogEvent.positionUpdate, { position: goal.title });
    return this.coreItemService.updateCoreItem$(goal);
  }

  addGoal$(subData: GoalSubData) {
    return this.coreItemService
      .batchWriteMetadataAndRelationList$({
        addMetadataList: [
          {
            coreType: CoreItemType.goal,
            subData,
          },
        ],
      })
      .pipe(
        map((arr) => {
          const goal = arr[0] as Goal;
          const goalBackend = this.coreItemService.convertFromFrontendToBackend(goal);

          posthog.capture(PostHogEvent.positionAdd, { position: goalBackend.title });

          this._changeSubject.next(goal);
          return goal;
        })
      );
  }

  deleteGoalList$(goalList: Goal[]) {
    const deleteMetadataList = goalList.map((c) => ({
      id: c.id,
      sk: c.sk,
    }));

    posthog.capture(PostHogEvent.positionDelete, { positionList: deleteMetadataList.map((c) => c.id).join(',') });

    return this.coreItemService
      .batchWriteMetadataAndRelationList$({
        deleteMetadataList,
      })
      .pipe(
        tap((_) => {
          this._changeSubject.next(undefined);
        })
      );
  }
}
